'use client';

import React from 'react';
import Image from 'next/image';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import Button from '../Button';

interface ShippingAvailabilityModalProps {
  open: boolean;
  onClose: () => void;
  onTryAnyway: () => void;
  externalLink: string;
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function isValidExternalLink(url: string): boolean {
  return url.startsWith('https://');
}

export default function ShippingAvailabilityModal({
  open,
  onClose,
  onTryAnyway,
  externalLink,
}: ShippingAvailabilityModalProps) {
  const t = useTranslations('ShippingAvailability');
  const domain = extractDomain(externalLink);
  const validLink = isValidExternalLink(externalLink);

  return (
    <Dialog open={open} className="relative z-50" onClose={onClose}>
      <div className="fixed inset-0 bg-neutral-100/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative w-full max-w-lg rounded-2xl bg-white px-6 pb-10 pt-8 shadow-xl sm:px-10">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-xl p-1 duration-200 ease-in hover:bg-neutral-10"
            aria-label={t('close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          <div className="flex flex-col items-center text-center">
            <Image src="/images/globe-icon.svg" alt="" width={56} height={56} className="mb-4" />

            <Dialog.Title as="h3" className="mb-3 text-xl font-bold text-neutral-100">
              {t('title')}
            </Dialog.Title>

            <p className="mb-8 max-w-sm text-sm text-neutral-60">
              {t.rich('description', {
                domain,
                b: (chunks) => <span className="font-bold text-neutral-100">{chunks}</span>,
              })}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={onTryAnyway}
                className="text-sm font-bold uppercase underline underline-offset-4 transition-colors hover:text-neutral-60"
              >
                {t('tryAnyway')}
              </button>

              {validLink && (
                <Button variant="primary" href={externalLink} openInNewTab>
                  {t('goToExternal', { domain: domain.toUpperCase() })}
                </Button>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
